import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FiX, FiChevronLeft, FiChevronRight, FiSettings } from 'react-icons/fi';
import { photosAPI } from '../utils/api';
import SEO from '../components/SEO';
import './Portfolio.css';

// Masaüstünde ~6 foto görünüyor; ilk batch = görünen + 1 scroll buffer
const BATCH_SIZE = 12;

const Portfolio = () => {
  const [photos, setPhotos] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [lightbox, setLightbox] = useState({ open: false, index: 0 });
  const [visibleItems, setVisibleItems] = useState(new Set());
  const itemRefs = useRef({});
  const loadMoreTriggerRef = useRef(null);

  // İlk batch: sadece görünen kadarı (order 0..11)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await photosAPI.getAll(
          { isPortfolio: 'true', limit: BATCH_SIZE, skip: 0, _: Date.now() },
          { cache: 'no-store' }
        );
        if (cancelled) return;
        const list = (res.photos || []).slice();
        list.sort((a, b) => {
          const oA = typeof a.order === 'number' ? a.order : 999999;
          const oB = typeof b.order === 'number' ? b.order : 999999;
          return oA - oB || String(a._id || '').localeCompare(String(b._id || ''));
        });
        setPhotos(list);
        setTotal(res.total != null ? res.total : list.length);
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to load photos:', error);
          setPhotos([]);
        }
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  // Scroll'da en alta yaklaşınca sonraki batch'i yükle
  useEffect(() => {
    if (loading || loadingMore || total === 0 || photos.length >= total) return;
    const el = loadMoreTriggerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        setLoadingMore(true);
        const skip = photos.length;
        photosAPI.getAll(
          { isPortfolio: 'true', limit: BATCH_SIZE, skip, _: Date.now() },
          { cache: 'no-store' }
        )
          .then((res) => {
            const nextBatch = (res.photos || []).slice();
            nextBatch.sort((a, b) => {
              const oA = typeof a.order === 'number' ? a.order : 999999;
              const oB = typeof b.order === 'number' ? b.order : 999999;
              return oA - oB || String(a._id || '').localeCompare(String(b._id || ''));
            });
            setPhotos((prev) => {
              const merged = [...prev, ...nextBatch];
              merged.sort((a, b) => {
                const oA = typeof a.order === 'number' ? a.order : 999999;
                const oB = typeof b.order === 'number' ? b.order : 999999;
                return oA - oB || String(a._id || '').localeCompare(String(b._id || ''));
              });
              return merged;
            });
          })
          .catch((err) => console.error('Failed to load more photos:', err))
          .finally(() => setLoadingMore(false));
      },
      { rootMargin: '200px', threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loading, total, photos.length, loadingMore]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleItems((prev) => new Set([...prev, entry.target.dataset.id]));
          }
        });
      },
      { threshold: 0.1, rootMargin: '100px' }
    );

    const timeoutId = setTimeout(() => {
      Object.values(itemRefs.current).forEach((ref) => {
        if (ref) observer.observe(ref);
      });
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, [photos]);

  const openLightbox = (index) => {
    setLightbox({ open: true, index });
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightbox({ open: false, index: 0 });
    document.body.style.overflow = 'unset';
  };

  const navigateLightbox = (direction) => {
    let newIndex = lightbox.index + direction;
    if (newIndex < 0) newIndex = photos.length - 1;
    if (newIndex >= photos.length) newIndex = 0;
    setLightbox({ open: true, index: newIndex });
  };

  useEffect(() => {
    if (!lightbox.open) return;

    const handleKeyPress = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') navigateLightbox(-1);
      if (e.key === 'ArrowRight') navigateLightbox(1);
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightbox]);

  if (loading) {
    return (
      <div className="portfolio-page">
        <SEO
          title="Portfolio"
          description="Professional photography portfolio by Marta Zborovska, Prague photographer. Wedding, portrait, fashion and commercial work. Book a session in Prague."
          path="/portfolio"
        />
        <div className="portfolio-empty">
          <p>Loading portfolio...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="portfolio-page">
      <SEO
        title="Portfolio"
        description="Professional photography portfolio by Marta Zborovska, Prague photographer. Wedding, portrait, fashion and commercial work. Book a session in Prague."
        path="/portfolio"
      />
      {photos.length === 0 ? (
        <div className="portfolio-empty">
          <h2>No photos yet</h2>
          <p>Upload photos from the Admin Panel to display them here.</p>
          <Link to="/admin" className="admin-link">
            <FiSettings /> Go to Admin Panel
          </Link>
        </div>
      ) : (
        <>
          <div className="portfolio-grid">
            {photos.map((photo, index) => (
              <div
                key={photo._id}
                ref={(el) => (itemRefs.current[photo._id] = el)}
                data-id={photo._id}
                className={`grid-item ${visibleItems.has(String(photo._id)) ? 'visible' : ''}`}
                onClick={() => openLightbox(index)}
              >
                <img 
                  src={photo.url} 
                  alt="" 
                  loading="lazy" 
                />
              </div>
            ))}
          </div>
          {photos.length < total && (
            <div
              ref={loadMoreTriggerRef}
              className="portfolio-load-more-sentinel"
              aria-hidden
            />
          )}
          {loadingMore && (
            <div className="portfolio-loading-more">Loading more...</div>
          )}
        </>
      )}

      {lightbox.open && photos[lightbox.index] && (
        <div className="portfolio-lightbox" onClick={closeLightbox}>
          <button className="lightbox-close" onClick={closeLightbox}>
            <FiX />
          </button>
          <button 
            className="lightbox-nav lightbox-prev"
            onClick={(e) => { e.stopPropagation(); navigateLightbox(-1); }}
          >
            <FiChevronLeft />
          </button>
          <button 
            className="lightbox-nav lightbox-next"
            onClick={(e) => { e.stopPropagation(); navigateLightbox(1); }}
          >
            <FiChevronRight />
          </button>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <img 
              src={photos[lightbox.index].url} 
              alt=""
            />
          </div>
          <div className="lightbox-counter">
            {lightbox.index + 1} / {photos.length}
          </div>
        </div>
      )}
    </div>
  );
};

export default Portfolio;
